import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  MenuItem,
  Select,
  FormControl,
  CircularProgress,
  IconButton,
  Stack,
  Divider,
  Backdrop,
  useTheme,
} from "@mui/material";
import {
  CompareArrows,
  ContentCopy,
  GTranslate,
  Translate,
  AutoAwesome,
  InfoOutlined,
  Bolt,
} from "@mui/icons-material";
import axios from "axios";
import API_BASE_URL from "../config";

const languages = [
  { code: "EN", name: "English" },
  { code: "TR", name: "Turkish" },
  { code: "DE", name: "German" },
  { code: "FR", name: "French" },
  { code: "ES", name: "Spanish" },
  { code: "IT", name: "Italian" },
  { code: "PT", name: "Portuguese" },
  { code: "NL", name: "Dutch" },
  { code: "PL", name: "Polish" },
  { code: "RU", name: "Russian" },
  { code: "JA", name: "Japanese" },
  { code: "ZH", name: "Chinese" },
];

export default function Translator(): JSX.Element {
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [targetLang, setTargetLang] = useState("TR");
  const [sourceLang, setSourceLang] = useState("Auto");
  const [detectedLangName, setDetectedLangName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingSeconds, setLoadingSeconds] = useState(0);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const handleTranslate = async (
    textToTranslate: string,
    targetLanguage: string,
  ) => {
    if (!textToTranslate.trim()) return;
    setLoading(true);
    setLoadingSeconds(0);
    const timer = setInterval(() => setLoadingSeconds(s => s + 1), 1000);
    try {
      const response = await axios.post(`${API_BASE_URL}/translate`, {
        text: textToTranslate,
        target_lang: targetLanguage,
      }, { timeout: 90000 }); // 90 seconds — accounts for Render free tier cold start
      if (response.data && response.data.result) {
        setOutputText(response.data.result);
        if (response.data.detected_language) {
          const detCode = response.data.detected_language.toUpperCase();
          const detLangObj = languages.find((l) => l.code === detCode);
          if (detLangObj) {
            setDetectedLangName(detLangObj.name);
          } else if (detCode !== "AUTO" && detCode.length > 0) {
            setDetectedLangName(detCode);
          } else {
            setDetectedLangName(null);
          }
        }
      } else if (response.data && response.data.error) {
        if (
          response.data.error.includes("429") ||
          response.data.error.includes("Quota exceeded")
        ) {
          setOutputText(
            "⚠️ API Rate limit reached (Free Tier). Please wait 1 minute before translating again.",
          );
        } else {
          setOutputText(`[Warning] Translation issue: ${response.data.error}`);
        }
      } else {
        setOutputText("[Error] Unexpected response from server.");
      }
    } catch (error: any) {
      console.error("Failed to translate:", error);
      setOutputText(
        `[Error] Failed to connect to translation server. Please try again later. Details: ${error.message || ""}`,
      );
    } finally {
      setLoading(false);
      clearInterval(timer);
      setLoadingSeconds(0);
    }
  };

  // Manual translation only to save quota
  useEffect(() => {
    if (!inputText.trim()) {
      setOutputText("");
      setDetectedLangName(null);
    }
  }, [inputText]);

  const handleSwap = () => {
    if (sourceLang !== "Auto") {
      const temp = sourceLang;
      setSourceLang(targetLang);
      setTargetLang(temp);
    }
    const tempText = inputText;
    setInputText(outputText);
    setOutputText(tempText);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(outputText);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", pt: 4 }}>
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 600,
            mb: 1,
            trackingTight: "-0.02em",
            color: isDarkMode ? "white" : "text.primary",
          }}
        >
          Language{" "}
          <span style={{ color: isDarkMode ? "#4b5563" : "#3b82f6" }}>
            Bridge
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: isDarkMode ? "#9ca3af" : "text.secondary",
            fontWeight: 400,
            maxWidth: 600,
          }}
        >
          Adapt your research between languages with technical precision.
        </Typography>
      </Box>

      {/* Controls Bar */}
      <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            p: 0.5,
            borderRadius: "12px",
            bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
            border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <FormControl
            size="small"
            variant="standard"
            sx={{ minWidth: 140, px: 1 }}
          >
            <Select
              value={sourceLang}
              onChange={(e) => setSourceLang(e.target.value)}
              disableUnderline
              sx={{
                color: isDarkMode ? "#9ca3af" : "text.secondary",
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              <MenuItem value="Auto">
                Auto-detect {detectedLangName ? `- ${detectedLangName}` : ""}
              </MenuItem>
              {languages.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <IconButton
            onClick={handleSwap}
            size="small"
            sx={{
              color: isDarkMode ? "#4b5563" : "text.secondary",
              "&:hover": { color: isDarkMode ? "white" : "primary.main" },
            }}
          >
            <CompareArrows fontSize="small" />
          </IconButton>

          <FormControl
            size="small"
            variant="standard"
            sx={{ minWidth: 140, px: 1 }}
          >
            <Select
              value={targetLang}
              onChange={(e) => setTargetLang(e.target.value)}
              disableUnderline
              sx={{
                color: isDarkMode ? "white" : "text.primary",
                fontSize: "13px",
                fontWeight: 600,
              }}
            >
              {languages.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              bgcolor: isDarkMode ? "#030303" : "background.paper",
              border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              height: { xs: "auto", md: 480 },
              display: "flex",
              flexDirection: "column",
              "&:focus-within": { borderColor: "#3b82f6" },
            }}
          >
            <Box sx={{ p: 3 }}>
              <TextField
                fullWidth
                multiline
                minRows={6}
                maxRows={12}
                placeholder="Enter text to translate..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontFamily: "inherit",
                    fontSize: "15px",
                    lineHeight: 1.7,
                    color: isDarkMode ? "white" : "text.primary",
                    alignItems: "flex-start",
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#4b5563" : "text.secondary" }}
              >
                {inputText.split(/\s+/).filter((x) => x).length} words
              </Typography>
              <Button
                variant="contained"
                onClick={() => handleTranslate(inputText, targetLang)}
                disabled={loading || !inputText.trim()}
                fullWidth={false}
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Bolt />
                  )
                }
                sx={{
                  borderRadius: "10px",
                  bgcolor: isDarkMode ? "white" : "primary.main",
                  color: isDarkMode ? "black" : "white",
                  textTransform: "none",
                  px: { xs: 3, md: 4 },
                  fontWeight: 600,
                  flexShrink: 0,
                  "&:hover": {
                    bgcolor: isDarkMode ? "#e5e7eb" : "primary.dark",
                  },
                  "&.Mui-disabled": {
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)",
                    color: isDarkMode
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.25)",
                  },
                }}
              >
                Translate
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
              border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              height: { xs: "auto", md: 480 },
              minHeight: { xs: 160, md: 480 },
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: 3, flexGrow: 1, overflowY: "auto" }}>
              <Typography
                sx={{
                  fontSize: "15px",
                  lineHeight: 1.7,
                  color: outputText
                    ? isDarkMode ? "white" : "text.primary"
                    : isDarkMode ? "#4b5563" : "text.secondary",
                  whiteSpace: "pre-wrap",
                }}
              >
                {outputText || "Translation will appear here..."}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderTop: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                bgcolor: isDarkMode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
              }}
            >
              <IconButton
                onClick={copyToClipboard}
                size="small"
                disabled={!outputText}
                sx={{
                  color: isDarkMode ? "#9ca3af" : "text.secondary",
                  "&:hover": {
                    color: isDarkMode ? "white" : "primary.main",
                    bgcolor: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
                  },
                }}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: "column",
          gap: 3,
          background: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(12px)",
          transition: "all 0.5s ease-in-out",
        }}
        open={loading}
      >
        <Box sx={{ position: "relative", width: 120, height: 120 }}>
          {/* Outer Pulse Ring */}
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              border: "2px solid rgba(59, 130, 246, 0.5)",
              animation: "pulse 2s infinite ease-out",
              "@keyframes pulse": {
                "0%": { transform: "scale(0.8)", opacity: 1 },
                "100%": { transform: "scale(1.5)", opacity: 0 },
              },
            }}
          />
          {/* Inner Spinner */}
          <Box
            sx={{
              position: "absolute",
              top: "10%",
              left: "10%",
              width: "80%",
              height: "80%",
              borderRadius: "50%",
              border: "3px solid transparent",
              borderTopColor: "#3b82f6",
              borderBottomColor: "#3b82f6",
              animation: "spin 1.5s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
          {/* Central Logo/Icon */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Bolt sx={{ fontSize: 40, color: "white", animation: "float 2s ease-in-out infinite" }} />
            <style>
              {`
                @keyframes float {
                  0%, 100% { transform: translateY(0); }
                  50% { transform: translateY(-5px); }
                }
              `}
            </style>
          </Box>
        </Box>
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="h5"
            sx={{
              color: "white",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              mb: 1,
              textShadow: "0 4px 12px rgba(0,0,0,0.3)"
            }}
          >
            {loadingSeconds < 8 ? "Translating..." : "Almost there..."}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.6)", fontWeight: 400 }}
          >
            {loadingSeconds < 5
              ? "Our AI is perfecting your text in real-time"
              : loadingSeconds < 20
              ? `Server is starting up... (${loadingSeconds}s)`
              : `Server waking up on free tier — usually under 60s (${loadingSeconds}s)`}
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
}
